import {
  DynamicPropertyType,
  EMPTY_VALUE,
  FieldSchema,
  FieldValue,
  FieldValues,
  isEmpty,
  PreparedDynamicStructure,
  prepareDynamicPropertyValue
} from "../Schema";
import { CustomState, Mapping } from "../common/DynamicManager";
import { isEqual } from "lodash";
import { DynamicValuesManager, DynamicValuesManagerState } from "./DynamicValuesManager";

export const DataTypes = {
  STRING: "string",
  BOOLEAN: "boolean",
  NUMBER: "number",
  DATE: "date",
  OBJECT: "object",
  ARRAY: "array",
};

export type DataType = typeof DataTypes[keyof typeof DataTypes];

export const DataVariants = {
  NORMAL: "normal",
  ENUM: "enum",
};

export type DataVariant = typeof DataVariants[keyof typeof DataVariants];

export type StandardEnumOption<
  T extends FieldValue = string
> = { value: T; label: string; };

export type EnumOption<
  T extends FieldValue = string
>  = StandardEnumOption<T> | string;

export const DataCardinalities = {
  SINGLE: "single",
  MULTIPLE: "multiple",
};

export type DataCardinality = typeof DataCardinalities[keyof typeof DataCardinalities];

export interface DataCardinalitySchema {
  cardinality?: DataCardinality;
  maxCount?: number;
  minCount?: number;
}

export interface DataBasicSchema extends DataCardinalitySchema {
  active?: boolean;
  assignedValue?: FieldValue;
  multiValued?: boolean;
}
export interface DataBasicState extends DynamicValuesManagerState, DataBasicSchema {}

export interface DataStringSchema extends DataBasicSchema {
  type?: typeof DataTypes.STRING;
  regex?: RegExp;
  maxLength?: number;
}

export interface DataBooleanSchema extends DataBasicSchema {
  type: typeof DataTypes.BOOLEAN;
}

export interface DataNumberSchema extends DataBasicSchema {
  type: typeof DataTypes.NUMBER;
}

export interface DataArraySchema extends DataBasicSchema {
  type: typeof DataTypes.ARRAY;
}

export type DataTypeSchema = DataStringSchema |
  DataBooleanSchema |
  DataNumberSchema |
  DataArraySchema;

export interface DataNormalSchema extends DataBasicSchema {
  variant?: typeof DataVariants.NORMAL;
}
export interface DataEnumSchema extends DataBasicSchema {
  variant: typeof DataVariants.ENUM;
  options: EnumOption<FieldValue>[];
  allowUnlisted?: boolean;
}

export type DataVariantSchema = DataNormalSchema | DataEnumSchema;

export type DataSchema = (DataTypeSchema & DataVariantSchema);

export type DataFieldState = DataBasicState & DataSchema;

export const defaultDataFieldState: DataFieldState = {
  loading: true,
  value: EMPTY_VALUE,
  type: DataTypes.STRING,
  variant: DataVariants.NORMAL
}

export interface DataState extends CustomState {
  values: FieldValues;
}

export const defaultDataState: DataState = {
  loading: true,
  values: {}
};

/** The shapes a stored date takes: `YYYY-MM-DD`, and the full timestamp GraphQL returns. */
const ISO_DATE = /^(\d{4}-\d{2}-\d{2})(T|$)/;

/**
 * Whether text is a date that exists.
 *
 * The shape is not enough. `32-13-2026` is a plausible typo for a day-first date and rearranges
 * into `2026-13-32`, which matches the ISO shape exactly — so a regex accepts it and the
 * thirteenth month reaches the database. Building the date and asking it to produce the same
 * text back is what rejects it: an out-of-range part rolls over into a different day.
 *
 * @param value - The text to judge.
 * @returns Whether it names a real calendar date.
 */
export const isIsoDate = (value: string): boolean => {
  const match = ISO_DATE.exec(value);
  if (!match) {
    return false;
  }
  const [, datePart] = match;
  const parsed = new Date(`${datePart}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().startsWith(datePart);
};

/**
 * Reduces a date-ish value to the calendar day it names, as `YYYY-MM-DD`.
 *
 * The form a date comparison should happen in. A stored date is a calendar day with no
 * timezone; `new Date()` — how every bound in the schema is written — is an instant carrying a
 * time of day. Comparing the two as instants makes a bound of "today" reject today itself,
 * which is neither what a date picker does with its own `maxDate` nor what the field means.
 * Reduced to this shape they compare as plain strings, lexicographically and therefore
 * chronologically, and the bound is inclusive.
 *
 * A `Date` is read in **local** parts and a string is taken as written. That asymmetry is
 * deliberate: a `Date` is an instant, and the question asked of it is which day it is *here*,
 * so `toISOString` would answer "yesterday" for a European user between midnight and 02:00.
 * A string is already a calendar day, and reading it literally is what `formatDateCell` does.
 *
 * `Date` is spelled out in the parameter because `FieldValue` does not include it, and both
 * things this reads do: a bound is declared as one, and a form's date picker puts one in a
 * value slot. `formatDateCell` guards for the same reason.
 *
 * @param value - The value to reduce.
 * @returns The calendar day, or `undefined` when the value does not name one.
 */
export const toCalendarDay = (value: FieldValue | Date): string | undefined => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ?
      undefined :
      [
        value.getFullYear(),
        `${value.getMonth() + 1}`.padStart(2, "0"),
        `${value.getDate()}`.padStart(2, "0"),
      ].join("-");
  }
  if (typeof value !== "string" || !isIsoDate(value)) {
    return undefined;
  }
  const [, datePart] = ISO_DATE.exec(value) as RegExpExecArray;
  return datePart;
};

/**
 * Whether a value is a usable instance of the field's declared type.
 *
 * Only strings are judged. A value of any other kind — a `Date`, a number, an object — came
 * from somewhere that already types it, and second-guessing that here would blank legitimate
 * form values. A string is the one shape that can arrive as free text, which in practice means
 * a CSV cell.
 *
 * @param field - The field whose declared type the value must satisfy.
 * @param value - The value to judge.
 * @returns Whether the value can stand as this type.
 */
export const isUsableAs = (field: FieldSchema, value: FieldValue): boolean => {
  if (typeof value !== "string") {
    return true;
  }
  return field.data?.type === DataTypes.DATE ?
    isIsoDate(value) :
    !!value && !Number.isNaN(Number(value));
};

/**
 * Wraps a value getter so text that is not a usable date or number is reduced to
 * `EMPTY_VALUE`.
 *
 * The same contract the enum variant applies to a cell matching no option, and it exists for
 * the same reason. Both CSV parsers deliberately keep text they could not read, so that
 * `validateCsvRow` can name the offending cell rather than let it vanish; this is the other
 * half of that bargain, reducing the text once it has been reported so it is never written.
 *
 * @param field - The field whose value is being coerced.
 * @param value - The value property of the mapping being extended.
 * @returns A getter yielding the value, or `EMPTY_VALUE` when it is unusable.
 */
const coerceTypedValue = (
  field: FieldSchema,
  value: DynamicPropertyType<FieldValue>
): DynamicPropertyType<FieldValue> => {
  const preparedValue = prepareDynamicPropertyValue(value);
  return {
    getter: async (values: FieldValues) => {
      const currentValue = await preparedValue.getter(values);
      if (isEmpty(currentValue) || isUsableAs(field, currentValue)) {
        return currentValue;
      }
      return EMPTY_VALUE;
    },
    fields: preparedValue.fields,
  };
};

/**
 * Manager for handling data-related state and logic based on a given schema.
 * It extends the SchemaManager to use its state management capabilities
 * while adding specific logic for handling data types and variants.
 */
export class DataManager extends DynamicValuesManager<DataFieldState> {

  /**
   * Custom state for the DataManager, which includes processing status,
   * current field values, and any other relevant information.
   */
  customState: DataState = defaultDataState;

  /**
   * Updates the custom state of the DataManager based on the current field states.
   * It sets the processing status and gathers the current values of all fields.
   */
  async updateCustomState() {
    this.customState = {
      loading: this.loading,
      values: this.getValues(),
    }
  }

  /**
   * Generates a mapping for the field based on its type.
   *
   * @param field - field schema to generate the mapping for.
   * @param basicMapping - the basic mapping to extend with type-specific properties.
   * @returns A mapping for the field state based on its type.
   */
  getTypeMapping(
    field: FieldSchema,
    basicMapping: Mapping<DataFieldState>
  ): Mapping<DataFieldState> {
    switch (field.data?.type) {
      case DataTypes.DATE:
      case DataTypes.NUMBER:
        return {
          ...basicMapping,
          // The state's own `type` stays STRING, as it has been for every field: rendering
          // decisions read `field.data.type` off the schema, and this state is consumed for
          // its value and options. Only the value is touched here.
          type: DataTypes.STRING,
          value: coerceTypedValue(field, basicMapping.value),
        };
      case DataTypes.STRING:
      default:
        return {
          ...basicMapping,
          type: DataTypes.STRING,
        };
    }
  }

  /**
   * Prepares enum options and bringing it to a proper format.
   *
   * @param options - The enum options to prepare.
   * @returns Prepared enum options.
   */

  prepareEnumOptions(
    options: DynamicPropertyType<EnumOption<FieldValue>[]>
  ): PreparedDynamicStructure<StandardEnumOption<FieldValue>[]> {
    const preparedOptions = prepareDynamicPropertyValue(options);
    return {
      getter: async (values) => {
        return (await preparedOptions.getter(values)).map((option) => {
          if (typeof option === "string") {
            return {
              value: option,
              label: option,
            }
          }
          return option;
        });
      },
      fields: preparedOptions.fields,
    }
  }

  /**
   * Generates a mapping for an enum variant field, including its options and value logic.
   *
   * @param data - The dynamic data containing the enum options.
   * @param typeMapping - The existing type mapping to extend with enum-specific properties.
   * @returns A mapping for the enum variant state.
   */
  getEnumMapping(
    field: FieldSchema,
    typeMapping: Mapping<DataFieldState>
  ): Mapping<DataFieldState> {
    const preparedOptions = this.prepareEnumOptions((field.data as DataEnumSchema)?.options);
    const preparedValue = prepareDynamicPropertyValue(
      typeMapping.value
    );
    return {
      ...typeMapping,
      variant: DataVariants.ENUM,
      options: preparedOptions,
      value: {
        getter: async (values: FieldValues) => {
          const options = await preparedOptions.getter(values);
          const value = await preparedValue.getter(values);

          if (value) {
            if (field.data?.cardinality === DataCardinalities.MULTIPLE) {
              if (Array.isArray(value) && value.length) {
                const allowUnlisted = (field.data as DataEnumSchema).allowUnlisted === true;
                return value
                  .map((item) => {
                    const listed = options
                      .find(
                        (option) => isEqual(option?.value, item)
                      )?.value;
                    return listed ?? (allowUnlisted ? item : undefined);
                  })
                  .filter(Boolean);
              }
            } else {
              if (options.find((option) => isEqual(option?.value, value))) {
                return value;
              } else {
                return EMPTY_VALUE;
              }
            }
          }
          return value;
        },
        fields: Array.from(new Set([
          ...(preparedValue.fields),
          ...(preparedOptions.fields)
        ]))
      },
    }
  }

  /**
   * Generates a mapping for the field based on its variant,
   * extending the already created mapping with variant-specific properties.
   *
   * @param field - field schema to generate the mapping for.
   * @param typeMapping - the existing mapping to extend with variant-specific properties.
   * @return A mapping for the field state generator based on its variant.
   */
  getVariantMapping(
    field: FieldSchema,
    typeMapping: Mapping<DataFieldState>
  ): Mapping<DataFieldState> {
    switch (field.data?.variant) {
      case DataVariants.ENUM:
        return this.getEnumMapping(
          field,
          typeMapping
        );
      case DataVariants.NORMAL:
      default:
        return {
          ...typeMapping,
          variant: DataVariants.NORMAL,
        };
    }
  }

  /**
   * Generates a basic mapping for the field.
   *
   * @param field - field schema to generate the mapping for.
   * @returns A basic mapping for the field state generator.
   */
  getBasicMapping(field: FieldSchema): Mapping<DataFieldState> {
    const preparedActive = prepareDynamicPropertyValue(
      field.data?.active ?? true,
    );
    const preparedAssignedValue = prepareDynamicPropertyValue(
      field.data?.assignedValue ?? undefined,
    );
    return {
      value: {
        getter: async (values: FieldValues) => {
          const currentActive = await preparedActive.getter(values);
          const currentAssignedValue = await preparedAssignedValue.getter(values);
          if (!currentActive) {
            return undefined;
          }
          return currentAssignedValue === undefined ?
            (
              isEmpty(values[field.name]) ?
                EMPTY_VALUE :
                values[field.name]
            ) :
            currentAssignedValue;
        },
        fields: Array.from(new Set([
          field.name,
          ...(preparedActive.fields),
          ...(preparedAssignedValue.fields)
        ]))
      },
      active: preparedActive,
    }
  }

  /**
   * General method to generate a mapping for the field based on its schema.
   *
   * @param field - field schema to generate the mapping for.
   * @returns A mapping for the field state generator based on its schema.
   */
  getMapping(field: FieldSchema): Mapping<DataFieldState> {
    return this.getVariantMapping(
      field,
      this.getTypeMapping(
        field,
        this.getBasicMapping(field)
      )
    );
  }
}
